import { CategoryIcon } from '@modules/common/components/CategoryIcon';
import { PredictionLabel } from '@modules/common/components/PredictionLabel';
import { HIGHLIGHT_ANIMATION_KEYFRAMES } from '@modules/feedback/components/AudioType/AudioFeedbackPlayer/constants';
import {
  AudioHighlightMetadata,
  AudioHighlightMetadataType,
} from '@modules/feedback/components/AudioType/types';
import { getHighlightTooltipComponentProps } from '@modules/feedback/components/AudioType/utils';
import { getFeedbackLabelIcon } from '@modules/feedback/components/FeedbackCard/utils/getFeedbackLabelIcon';
import { useIsFeedbackOnFeedbackEnabled } from '@modules/feedback/hooks';
import { useIsFeedbackRedesignEnabled } from '@modules/feedbackV2/common/hooks';
import { Chip, Stack, Tooltip, Typography } from '@mui/material';
import { ReasonOrEntityLabelType } from 'v2/core/entities/feedback/labels';

interface AudioHighlightChipProps {
  highlight: AudioHighlightMetadata;
  maxWidth?: number | 'auto';
  shouldAnimate?: boolean;
  shouldShowSummaryIcon?: boolean;
  shouldShowSummaryText?: boolean;
  shouldShowLabelTooltip?: boolean;
  label?: ReasonOrEntityLabelType;
}

export const AudioHighlightChip = ({
  highlight,
  maxWidth = 250,
  shouldAnimate = false,
  shouldShowSummaryText = false,
  shouldShowLabelTooltip,
  label,
}: AudioHighlightChipProps) => {
  const isReasonSummaryType =
    highlight.type === AudioHighlightMetadataType.ReasonSummary;

  const isKeywordSummaryType =
    highlight.type === AudioHighlightMetadataType.KeywordSummary ||
    isReasonSummaryType;

  const { isFeedbackOnFeedbackEnabled } = useIsFeedbackOnFeedbackEnabled();

  const isFeedbackRedesignEnabled = useIsFeedbackRedesignEnabled();

  const renderTooltipContent = () => {
    if (!isKeywordSummaryType || !shouldShowSummaryText) {
      return shouldShowLabelTooltip ? highlight.labelDisplayName : '';
    }
    return (
      <Stack p={1} spacing={1}>
        <Typography variant="caption">{highlight.summaryText}</Typography>
        {highlight.summaryCount > 1 && (
          <Typography variant="caption">
            ( {highlight.summaryIndex + 1} / {highlight.summaryCount} )
          </Typography>
        )}
      </Stack>
    );
  };

  return (
    <Tooltip
      arrow
      title={renderTooltipContent()}
      placement="top"
      componentsProps={getHighlightTooltipComponentProps(400)}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        {label &&
          (label.type === 'REASON' ? (
            <CategoryIcon category={label.category ?? null} size={14} /> // TODO: remove fallback after contract update
          ) : (
            <Stack direction="row">
              {label.entityUnits?.map((entity) => (
                <CategoryIcon
                  category={entity.category ?? null}
                  size={14}
                  key={entity.category}
                /> // TODO: remove fallback after contract update
              ))}
            </Stack>
          ))}
        {isFeedbackOnFeedbackEnabled && label ? (
          <PredictionLabel label={label} size="small" />
        ) : (
          <Chip
            size="small"
            variant="outlined"
            label={
              label ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  {getFeedbackLabelIcon({
                    label,
                    shouldShowPrimaryColor: !isFeedbackRedesignEnabled,
                  })}
                  {highlight.labelDisplayName}
                </Stack>
              ) : (
                highlight.labelDisplayName
              )
            }
            sx={{
              maxWidth,
              animation: shouldAnimate
                ? `${HIGHLIGHT_ANIMATION_KEYFRAMES} 0.7s linear infinite alternate`
                : 'none',
              '&. MuiChip-label': {
                display: 'block',
              },
            }}
          />
        )}
      </Stack>
    </Tooltip>
  );
};
